/**
 * Times: multiplication as a rectangle you drag out with a finger.
 *
 * Small numbers fill with squares you can literally count. Past that the
 * rectangle cuts itself along the tens — 24 x 37 becomes 20x30, 20x7, 4x30 and
 * 4x7 — which is long multiplication, drawn instead of written.
 */

import { useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, RotateCcw } from 'lucide-preact';
import { token } from '../../engine/palette.ts';
import { play } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { partials, shouldCount } from '../../lib/partials.ts';
import { groupDigits } from '../../lib/bignum.ts';
import { Coach, Stepper, ToyButton } from '../../ui/controls.tsx';
import { NumberPad } from '../../ui/NumberPad.tsx';

const MAX_SIDE = 999;
/** Beyond this a finger-drag stops being the useful way to choose. */
const DRAG_MAX = 30;

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

const PART_COLORS = ['--crayon-teal', '--crayon-sky', '--crayon-purple', '--crayon-orange', '--crayon-green', '--crayon-red'];

export function TimesToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef({ x: 0, y: 0, unit: 1 });

  const [across, setAcross] = useState(4);
  const [down, setDown] = useState(3);
  const [editing, setEditing] = useState<'across' | 'down' | null>(null);
  const live = useRef({ across, down });
  live.current = { across, down };

  const product = across * down;
  const parts = partials(across, down);
  const counting = shouldCount(across, down);

  useSurface(canvasRef, {
    onFrame: (ctx, _dt, { width, height }) => {
      const { across: a, down: b } = live.current;
      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      // Leave room down the left for the side label — "337" written outside a
      // rectangle that starts 30px in gets its first digit shaved off.
      const padX = 26 + String(b).length * 11;
      // Below the readout pill, which floats over this canvas — the number
      // written along the top edge has to clear it.
      const padTop = 158;
      const padBottom = 56;
      const boxW = width - padX * 2;
      const boxH = height - padTop - padBottom;
      const unit = Math.min(boxW / a, boxH / b);
      const w = unit * a;
      const h = unit * b;
      const x = width / 2 - w / 2;
      const y = padTop + (boxH - h) / 2;
      frameRef.current = { x, y, unit };

      if (shouldCount(a, b)) {
        // Every square drawn, because at this size they are meant to be counted.
        const color = token('--crayon-teal');
        for (let c = 0; c < a; c++) {
          for (let r = 0; r < b; r++) {
            ctx.fillStyle = color;
            ctx.strokeStyle = 'rgba(44, 37, 69, 0.75)';
            ctx.lineWidth = Math.max(1.5, unit * 0.05);
            ctx.beginPath();
            ctx.roundRect(x + c * unit + unit * 0.06, y + r * unit + unit * 0.06, unit * 0.88, unit * 0.88, unit * 0.2);
            ctx.fill();
            ctx.stroke();
          }
        }
      } else {
        // Cut along the tens and label each piece with its own multiplication.
        let offsetX = 0;
        const columns = splitOf(a);
        const rows = splitOf(b);
        columns.forEach((pa, ci) => {
          let offsetY = 0;
          rows.forEach((pb, ri) => {
            const pw = unit * pa;
            const ph = unit * pb;
            ctx.fillStyle = token(PART_COLORS[(ci + ri) % PART_COLORS.length]);
            ctx.globalAlpha = 0.9;
            ctx.fillRect(x + offsetX, y + offsetY, pw, ph);
            ctx.globalAlpha = 1;
            ctx.strokeStyle = token('--paper');
            ctx.lineWidth = 3;
            ctx.strokeRect(x + offsetX, y + offsetY, pw, ph);

            const label = `${pa} x ${pb} = ${groupDigits(BigInt(pa * pb))}`;
            const size = Math.min(pw / (label.length * 0.56), ph * 0.4, 20);
            if (size >= 8) {
              ctx.save();
              ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.font = `800 ${size}px ui-rounded, system-ui, sans-serif`;
              ctx.fillText(label, x + offsetX + pw / 2, y + offsetY + ph / 2);
              ctx.restore();
            }
            offsetY += ph;
          });
          offsetX += unit * pa;
        });
      }

      // The two sides, written along the edges.
      ctx.save();
      ctx.fillStyle = token('--ink-soft');
      ctx.textAlign = 'center';
      ctx.font = `800 ${Math.min(22, width * 0.055)}px ui-rounded, system-ui, sans-serif`;
      ctx.fillText(String(a), x + w / 2, y - 14);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(b), x - 12, y + h / 2);
      ctx.restore();
    },

    onDown: (p, size) => dragTo(p.pos.x, p.pos.y, size.width),
    onMove: (p, size) => dragTo(p.pos.x, p.pos.y, size.width),
  });

  /** Dragging out from the top-left corner picks both sides at once. */
  function dragTo(px: number, py: number, width: number): void {
    const { x, y, unit } = frameRef.current;
    if (unit <= 0 || width === 0) return;
    const a = Math.max(1, Math.min(DRAG_MAX, Math.round((px - x) / unit)));
    const b = Math.max(1, Math.min(DRAG_MAX, Math.round((py - y) / unit)));
    if (a !== live.current.across || b !== live.current.down) {
      play('count', 0.35);
      setAcross(a);
      setDown(b);
    }
  }

  const reset = () => {
    setAcross(4);
    setDown(3);
    play('whoosh', 0.6);
  };

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Multiplication rectangle" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <ToyButton round label="Start again" hideLabel onClick={reset}>
            <RotateCcw size={24} strokeWidth={2.5} />
          </ToyButton>
        </div>

        <p class="readout">
          <strong>
            {across} x {down} = {groupDigits(BigInt(product))}
          </strong>
          <span>{counting ? 'count the squares' : `${parts.length} rectangles added up`}</span>
        </p>

        <Coach>{counting ? 'Drag on the paper to change both sides' : 'Each piece is one easy times'}</Coach>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row">
          <Stepper value={across} min={1} max={MAX_SIDE} label="across" onChange={setAcross} onPick={() => setEditing('across')} />
          <div class="dock__spacer" />
          <Stepper value={down} min={1} max={MAX_SIDE} label="down" onChange={setDown} onPick={() => setEditing('down')} />
        </div>
      </div>

      {editing && (
        <NumberPad
          title={editing === 'across' ? 'How many across?' : 'How many down?'}
          value={editing === 'across' ? across : down}
          min={1}
          max={MAX_SIDE}
          onDone={(value) => {
            if (editing === 'across') setAcross(value);
            else setDown(value);
            setEditing(null);
          }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** The tens split, but never fewer than one piece to draw. */
function splitOf(n: number): number[] {
  const digits = String(Math.max(1, Math.floor(n)));
  const out: number[] = [];
  for (let i = 0; i < digits.length; i++) {
    const value = Number(digits[i]) * 10 ** (digits.length - 1 - i);
    if (value > 0) out.push(value);
  }
  return out.length > 0 ? out : [n];
}
